import {
  fetchAPI,
  TASKS_SERVICE_URL,
} from "@coworker/app/src/hooks/API/tasks.service.helper";
import { fetchAPI as fetchDataService } from "@coworker/app/src/hooks/API/data.service";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { FetchApiResponse } from "@coworker/apprestructured/src/shared/types/fetchApiResponseType";
import { INTERVAL } from "@coworker/app/src/constants/clientTime";

type AmountSettings = {
  pieces_per_multi_pack: string | null;
  pieces_per_pallet: string | null;
};

export async function fetchItemAmountSettings(
  itemNo: string,
  enablePalletMultiPack: boolean
) {
  const encodedItemNo = encodeURIComponent(itemNo);

  if (enablePalletMultiPack) {
    const url = `pallet-multipack/getPalletMultipack?itemNo=${encodedItemNo}`;
    const response = await fetchDataService(url, "GET", "");
    return response;
  }

  const url = `${TASKS_SERVICE_URL}/v1/shared/amount_settings?short_id=${encodedItemNo}`;
  const { result } = await fetchAPI("GET", url);
  return result;
}

async function updateItemAmountSettings(
  itemNo: string,
  amounts: AmountSettings
) {
  const url = `${TASKS_SERVICE_URL}/v1/shared/amount_settings?short_id=${itemNo}`;
  const bodyData = {
    short_id: itemNo,
    // TODO: Will parseInt return NaN here if we have bad data from frontend / from previously stored data?
    pieces_per_multi_pack:
      amounts?.pieces_per_multi_pack !== "" &&
      amounts?.pieces_per_multi_pack !== null
        ? parseInt(amounts?.pieces_per_multi_pack)
        : null,
    pieces_per_pallet:
      amounts?.pieces_per_pallet !== "" && amounts?.pieces_per_pallet !== null
        ? parseInt(amounts?.pieces_per_pallet)
        : null,
  };
  const { result } = (await fetchAPI("PUT", url, bodyData)) as FetchApiResponse;
  return result;
}

export function useItemAmountSettings(
  itemNo: string,
  enablePalletMultiPack: boolean = false
) {
  return useQuery({
    queryKey: ["Item", "amountSettings", itemNo, enablePalletMultiPack],
    queryFn: () => fetchItemAmountSettings(itemNo, enablePalletMultiPack),
    staleTime: INTERVAL.DAY, // 1 day, considered fresh for a day.
    gcTime: INTERVAL.DAY * 7, // 7 days, garbage collect after a week.
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    enabled: !!itemNo && enablePalletMultiPack !== undefined,
  });
}

export const useUpdateItemAmountSettings = (itemNo: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (amounts: AmountSettings) =>
      updateItemAmountSettings(itemNo, amounts),
    onSuccess: () => {
      return queryClient.invalidateQueries({
        queryKey: ["Item", "amountSettings", itemNo],
      });
    },
  });
};
